[file name]: EngineerAssignmentModal.vue
<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import type { Tender } from '@/types/tender'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import TextareaDefault from '@/components/forms/TextArea/TextAreaDefault.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'

interface Engineer {
  id: string
  name: string
  email: string
  phone: string
  expertise: string[]
  currentWorkload: number
  maxWorkload: number
  first_name?: string
  last_name?: string
  role?: string
  department?: string
  created_at?: string
  updated_at?: string
}

interface Props {
  tender: Tender
  currentEngineerId?: string  // Add this prop for currently assigned engineer
}

interface Emits {
  (e: 'assign', data: {
    tenderId: string;
    engineerId: string;
    engineerName: string;
    notes: string
  }): void
  (e: 'cancel'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Form state
const selectedEngineerId = ref('')
const assignmentNotes = ref('')

// Engineer data
const engineers = ref<Engineer[]>([])
const loadingEngineers = ref(false)
const engineerError = ref<string | null>(null)

// Form validation
const engineerValidationError = ref('')
const notesValidationError = ref('')

// Define proper types for engineer data from server
interface ServerEngineer {
  id: number
  username?: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  role?: string
  department?: string
  expertise?: string
  current_workload?: number
  max_workload?: number
  created_at?: string
  updated_at?: string
}

// Fetch engineers from server using centralized api
const fetchEngineers = async () => {
  try {
    console.log('🔄 Fetching engineers from server...')
    engineerError.value = null
    loadingEngineers.value = true

    const response = await api.get('/api/users/engineers')

    console.log('📥 Engineers API response:', {
      status: response.status,
      data: response.data,
      success: response.data.success,
      engineersCount: response.data.engineers?.length
    })

    if (response.data.success && response.data.engineers) {
      // Transform the data to match your Engineer interface
      engineers.value = response.data.engineers.map((engineer: ServerEngineer) => ({
        id: engineer.id.toString(),
        name: `${engineer.first_name || ''} ${engineer.last_name || ''}`.trim() || engineer.username || engineer.email,
        email: engineer.email,
        phone: engineer.phone || 'N/A',
        expertise: engineer.expertise ? engineer.expertise.split(',').map((exp: string) => exp.trim()) : [],
        currentWorkload: engineer.current_workload || 0,
        maxWorkload: engineer.max_workload || 10,
        first_name: engineer.first_name,
        last_name: engineer.last_name,
        role: engineer.role,
        department: engineer.department,
        created_at: engineer.created_at,
        updated_at: engineer.updated_at
      }))
      console.log(`✅ Found ${engineers.value.length} engineers`)
    } else {
      throw new Error(response.data.error || 'No engineers found')
    }

  } catch (err) {
    console.error('❌ Error fetching engineers:', err)
    engineerError.value = getAxiosErrorMessage(err)
    engineers.value = []
  } finally {
    loadingEngineers.value = false
  }
}

// Convert engineers to SelectOptionPrime format - EXCLUDE CURRENT ENGINEER
const engineerOptions = computed(() => {
  return engineers.value
    .filter(engineer => engineer.id !== props.currentEngineerId) // Filter out current engineer
    .map((engineer) => ({
      label: `${engineer.name} (${engineer.email})${getWorkloadText(engineer)}`,
      value: engineer.id,
      description: engineer.role === 'engineer' ? 'Engineer' : engineer.role || 'N/A',
      disabled: engineer.id === props.currentEngineerId // Mark as disabled if it's current engineer
    }))
})

// Get current engineer details
const currentEngineer = computed(() => {
  if (!props.currentEngineerId) return null
  return engineers.value.find(engineer => engineer.id === props.currentEngineerId)
})

// Helper functions for workload
const getWorkloadPercentage = (engineer: Engineer) => {
  if (!engineer.maxWorkload) return 0
  return Math.round((engineer.currentWorkload / engineer.maxWorkload) * 100)
}

const getWorkloadText = (engineer: Engineer) => {
  const percentage = getWorkloadPercentage(engineer)
  if (percentage >= 80) return ' (High Workload)'
  if (percentage >= 60) return ' (Medium Workload)'
  if (percentage >= 40) return ' (Normal Workload)'
  return ' (Low Workload)'
}

// Format date for display
// const formatDate = (dateString: string): string => {
//   if (!dateString) return 'N/A'
//   try {
//     const date = new Date(dateString)
//     return date.toLocaleDateString('en-IN', {
//       day: '2-digit',
//       month: 'short',
//       year: 'numeric'
//     })
//   } catch {
//     return dateString
//   }
// }

// Get selected engineer details
const selectedEngineer = computed(() => {
  return engineers.value.find(engineer => engineer.id === selectedEngineerId.value)
})

// Validation functions
const validateEngineer = () => {
  if (!selectedEngineerId.value) {
    engineerValidationError.value = 'Please select an engineer'
    return false
  }
  engineerValidationError.value = ''
  return true
}

const validateNotes = () => {
  if (!assignmentNotes.value.trim()) {
    notesValidationError.value = 'Assignment notes are required'
    return false
  }
  if (assignmentNotes.value.trim().length < 10) {
    notesValidationError.value = 'Notes must be at least 10 characters long'
    return false
  }
  notesValidationError.value = ''
  return true
}

// Combined validation - exposed to parent
const validateForm = () => {
  const isEngineerValid = validateEngineer()
  const isNotesValid = validateNotes()
  return isEngineerValid && isNotesValid
}

// Submit function - exposed to parent
const submitForm = () => {
  const isValid = validateForm()

  if (!isValid) {
    return false // Validation failed
  }

  // Get the selected engineer's name
  const selectedEngineerObj = engineers.value.find(engineer => engineer.id === selectedEngineerId.value)
  const engineerName = selectedEngineerObj?.name || `Engineer ${selectedEngineerId.value.substring(0, 8)}`

  // Emit the assign event with validated data INCLUDING engineerName
  emit('assign', {
    tenderId: props.tender.t247Id,
    engineerId: selectedEngineerId.value,
    engineerName: engineerName, // Make sure this is included
    notes: assignmentNotes.value
  })

  return true // Submission successful
}

// Handle engineer selection change
const handleEngineerChange = (value: string) => {
  selectedEngineerId.value = value
  // Clear error when user selects an engineer
  if (value) {
    engineerValidationError.value = ''
  }
}

// Handle notes change
const handleNotesChange = (value: string) => {
  assignmentNotes.value = value
  // Clear error when user types
  if (value.trim().length >= 10) {
    notesValidationError.value = ''
  }
}

// Initialize - fetch engineers
onMounted(() => {
  console.log('🔍 EngineerAssignmentModal mounted')
  fetchEngineers()
})

// Watch for tender changes
watch(() => props.tender, (newTender) => {
  if (newTender) {
    // Clear form when new tender is selected
    selectedEngineerId.value = ''
    assignmentNotes.value = ''
    engineerValidationError.value = ''
    notesValidationError.value = ''
  }
})

// Watch for currentEngineerId changes
watch(() => props.currentEngineerId, (newCurrentEngineerId) => {
  console.log('Current engineer ID changed:', newCurrentEngineerId)
  if (newCurrentEngineerId) {
    // Clear selected engineer if it's the same as current engineer
    if (selectedEngineerId.value === newCurrentEngineerId) {
      selectedEngineerId.value = ''
    }
  }
})

// Expose methods to parent component
defineExpose({
  validateForm,
  submitForm
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Tender Information -->
    <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
      <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Tender Details</h4>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Tender ID</p>
          <p class="text-sm font-medium">{{ tender.t247Id }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Organization</p>
          <p class="text-sm font-medium">{{ tender.organization }}</p>
        </div>
        <div class="col-span-2">
          <p class="text-xs text-gray-500 dark:text-gray-400">Description</p>
          <p class="text-sm font-medium line-clamp-2">{{ tender.tenderBrief }}</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <LoaderDefault
      v-if="loadingEngineers"
      title="Loading engineers from database..."
      subtitle="Please wait while we fetch available engineers"
    />

    <!-- Engineer Selection -->
    <div class="space-y-3" v-else-if="!engineerError && engineers.length > 0">
      <div class="flex items-center justify-between">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Select Engineer <span class="text-red-500">*</span>
        </label>
        <span class="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
          {{ engineers.length }} Engineers Available
        </span>
      </div>

      <!-- Show current engineer info if exists -->
      <div v-if="currentEngineer && currentEngineerId" class="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
        <p class="text-sm font-medium text-amber-800 mb-1">Currently Assigned Engineer:</p>
        <p class="text-sm text-amber-700">{{ currentEngineer.name }} ({{ currentEngineer.email }})</p>
        <p class="text-xs text-amber-600 mt-1">This engineer is already assigned to this tender and cannot be selected again.</p>
      </div>

      <SelectOptionPrime
        v-model="selectedEngineerId"
        :options="engineerOptions"
        id="engineerSelect"
        placeholder="-- Select a different engineer --"
        :asterisk="true"
        :errorMessage="engineerValidationError"
        @update:modelValue="handleEngineerChange"
        parentClass="w-full"
        size="md"
        :disabled="loadingEngineers"
      />

      <!-- Selected Engineer Details -->
      <div v-if="selectedEngineer" class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p class="text-sm font-medium text-blue-800 mb-1">New Engineer to Assign:</p>
        <p class="text-sm text-blue-700">{{ selectedEngineer.name }} ({{ selectedEngineer.email }})</p>
        <p class="text-xs text-blue-600">Phone: {{ selectedEngineer.phone }}</p>
        <!-- <p class="text-xs text-blue-600">Department: {{ selectedEngineer.department || 'N/A' }}</p>
        <p class="text-xs text-blue-600" v-if="selectedEngineer.created_at">
          Member since: {{ formatDate(selectedEngineer.created_at) }}
        </p> -->
      </div>
    </div>

    <!-- No Engineers Available (excluding current engineer) -->
    <div v-else-if="!loadingEngineers && !engineerError && engineers.length === 1 && currentEngineer" class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <p class="text-sm text-yellow-700 font-medium mb-1">Only Current Engineer Available</p>
      <p class="text-sm text-yellow-600">
        The only engineer available is already assigned to this tender.
        Please add more engineers to the system to reassign this tender.
      </p>
      <div class="flex gap-2 mt-2">
        <button
          @click="fetchEngineers"
          class="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md transition-colors"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- No Engineers Available (general) -->
    <div v-else-if="!loadingEngineers && !engineerError && engineers.length === 0" class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <p class="text-sm text-yellow-700 font-medium mb-1">No Engineers Available</p>
      <p class="text-sm text-yellow-600">
        No active engineers found in the database. Please add engineers before assigning tenders.
      </p>
      <div class="flex gap-2 mt-2">
        <button
          @click="fetchEngineers"
          class="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md transition-colors"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- Engineer Error State -->
    <div v-else-if="engineerError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-sm font-medium text-red-800 mb-2">Failed to Load Engineers</p>
      <p class="text-sm text-red-700 mb-3">{{ engineerError }}</p>

      <div class="flex flex-col gap-2 mt-3">
        <div class="flex gap-2">
          <button
            @click="fetchEngineers"
            class="flex-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-md transition-colors"
          >
            Retry
          </button>
        </div>

        <div class="flex gap-2">
          <button
            @click="$emit('cancel')"
            class="flex-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Assignment Notes (only show if engineers loaded successfully) -->
    <div v-if="!loadingEngineers && !engineerError && engineers.length > 0" class="space-y-2">
      <label for="assignmentNotes" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Assignment Notes <span class="text-red-500">*</span>
      </label>
      <TextareaDefault
        v-model="assignmentNotes"
        id="assignmentNotes"
        name="assignmentNotes"
        placeholder="Enter notes about this assignment (e.g., technical requirements, priorities, etc.)"
        :rows="4"
        :errorMessage="notesValidationError"
        @update:modelValue="handleNotesChange"
        parentClass="w-full"
      />
      <p class="text-xs text-gray-500">
        Minimum 10 characters. These notes will be visible to the assigned engineer.
      </p>
    </div>

    <!-- Validation Summary -->
    <div v-if="engineerValidationError || notesValidationError" class="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <p class="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Please fix the following errors:</p>
      <ul class="text-xs text-red-600 dark:text-red-400 space-y-1">
        <li v-if="engineerValidationError">• {{ engineerValidationError }}</li>
        <li v-if="notesValidationError">• {{ notesValidationError }}</li>
      </ul>
    </div>

    <!-- Instructions -->
    <div v-if="!loadingEngineers && !engineerError && engineers.length > 0" class="p-3 bg-gray-50 border border-gray-200 rounded-lg">
      <p class="text-sm font-medium text-gray-700 mb-1">Assignment Instructions:</p>
      <ul class="text-xs text-gray-600 space-y-1">
        <li>• Once assigned, this tender will move to the Engineer Tenders page</li>
        <li>• The engineer will receive a notification about this assignment</li>
        <li>• The tender will no longer appear in the main dashboard list</li>
        <li>• You can track progress in the Engineer Tenders section</li>
        <li v-if="currentEngineer">• Current engineer will be replaced with the new engineer</li>
      </ul>
    </div>

    <!-- Cannot Assign Warning -->
    <div v-if="!loadingEngineers && !engineerError && engineers.length === 0" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-sm font-medium text-red-800 mb-2">Cannot Assign Tender</p>
      <p class="text-sm text-red-700">
        You cannot assign this tender because there are no active engineers available.
        Please add engineers to the system first.
      </p>
    </div>
  </div>
</template>
